"use client";

import React, { useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import Cookies from "js-cookie";
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa";

const formSchema = z.object({
  username: z.string().min(1, { message: "Username is required" }),
  password: z
    .string()
    .min(8, { message: "Password must be at least 8 characters" }),
});

type AuthFormValues = z.infer<typeof formSchema>;

interface AuthFormProps {
  websiteId: string;
}

export const SiteAuthScreen: React.FC<AuthFormProps> = ({ websiteId }) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const disabled = loading;
  const year = new Date().getFullYear();

  const form = useForm<AuthFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: "",
      password: "",
    },
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<AuthFormValues> = async (data) => {
    try {
      setLoading(true);
      const response = await fetch(
        "https://backend-site-auth.onrender.com/api/v1/auth-external",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            siteId: websiteId,
            authUsername: data.username,
            authPassword: data.password,
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        alert("Login Successful: You have logged in successfully");
        console.log(result);
        Cookies.set("site-auth-token", result.token, {
          expires: new Date(Date.now() + 6 * 60 * 60 * 1000),
          path: "/",
          secure: window.location.protocol === "https:",
          sameSite: "Lax",
        });
        window.location.reload();
      } else {
        alert(`Login Failed: ${result.message}`);
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Login Failed: Invalid username or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="bg-gray-50 dark:bg-[#131626]">
        <div className="absolute z-20 mt-5 flex h-14 w-full items-center justify-between px-5 md:px-24">
          <div className="relative flex items-center text-lg font-medium text-gray-900 dark:text-white">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mr-2 h-6 w-6"
              color="#78d6ce"
            >
              <path d="M15 6v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3" />
            </svg>
            SiteAuth | Authentication
          </div>
        </div>
        <div className="relative h-screen flex-col items-center justify-center lg:px-0">
          <div className="flex h-full items-center p-4 lg:p-8">
            <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[350px]">
              <div className="flex flex-col space-y-2 text-center text-gray-900 dark:text-white">
                <h1 className="text-2xl font-semibold tracking-tight">
                  Authenticate
                </h1>
                <p className="text-sm text-muted-foreground">
                  Enter the details to access the website
                </p>
              </div>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="w-full space-y-2"
              >
                <div>
                  <label className="block text-left text-black dark:text-white">
                    Username
                  </label>
                  <input
                    disabled={loading}
                    placeholder="Enter your username"
                    {...form.register("username")}
                    className="w-full mt-1 p-2 border rounded focus:outline-none focus:ring focus:border-gray-700 dark:bg-gray-100 dark:focus:border-gray-700 dark:focus:ring dark:text-gray-900 dark:placeholder-gray-700 dark:hover:bg-gray-200 dark:hover:text-gray-900 dark:hover:border-gray-700 dark:focus:bg-gray-200 dark:focus:text-gray-900 dark:disabled:bg-gray-200 dark:disabled:text-gray-900 dark:disabled:border-gray-700 dark:disabled:placeholder-gray-700 dark:disabled:hover:bg-gray-200 dark:disabled:hover:text-gray-900 dark:disabled:hover:border-gray-700 dark:disabled:focus:bg-gray-200 dark:disabled:focus:text-gray-900  dark:disabled:focus:ring-gray-700 dark:disabled:focus:ring-opacity-50 dark:disabled:focus:ring-offset-gray-200 dark:disabled:focus:ring-offset-opacity-50"
                  />
                  {form.formState.errors.username && (
                    <p className="text-red-500 text-sm mt-1">
                      {form.formState.errors.username.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-left text-black dark:text-white">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      disabled={loading}
                      placeholder="Enter your password"
                      {...form.register("password")}
                      className="hide-password-toggle pr-10 w-full mt-1 p-2 border rounded focus:outline-none focus:ring focus:border-gray-700 dark:bg-gray-100 dark:focus:border-gray-700 dark:focus:ring dark:text-gray-900 dark:placeholder-gray-700 dark:hover:bg-gray-200 dark:hover:text-gray-900 dark:hover:border-gray-700 dark:focus:bg-gray-200 dark:focus:text-gray-900 dark:disabled:bg-gray-200 dark:disabled:text-gray-900 dark:disabled:border-gray-700 dark:disabled:placeholder-gray-700 dark:disabled:hover:bg-gray-200 dark:disabled:hover:text-gray-900 dark:disabled:hover:border-gray-700 dark:disabled:focus:bg-gray-200 dark:disabled:focus:text-gray-900  dark:disabled:focus:ring-gray-700 dark:disabled:focus:ring-opacity-50 dark:disabled:focus:ring-offset-gray-200 dark:disabled:focus:ring-offset-opacity-50"
                    />
                    <button
                      type="button"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparents"
                      onClick={() => setShowPassword((prev) => !prev)}
                      disabled={disabled}
                    >
                      {showPassword && !disabled ? (
                        <FaRegEye className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        <FaRegEyeSlash className="h-4 w-4" aria-hidden="true" />
                      )}
                      <span className="sr-only">
                        {showPassword ? "Hide password" : "Show password"}
                      </span>
                    </button>
                    {/* hides browsers password toggles */}
                    <style>{`
					.hide-password-toggle::-ms-reveal,
					.hide-password-toggle::-ms-clear {
						visibility: hidden;
						pointer-events: none;
						display: none;
					}
				`}</style>
                  </div>
                  {form.formState.errors.password && (
                    <p className="text-red-500 text-sm mt-1">
                      {form.formState.errors.password.message}
                    </p>
                  )}
                </div>
                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full mt-4 p-2 text-white rounded bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring focus:border-gray-700 dark:bg-gray-100 dark:hover:bg-gray-200 dark:text-gray-900 dark:focus:ring dark:focus:border-gray-700"
                  >
                    {loading ? "Loading..." : "Login"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        <footer className="absolute bottom-0 w-full p-4 text-center text-gray-500">
          <p>
            Secure every deployment with{" "}
            <a
              href="https://site-auth.sahibsingh.dev"
              target="_blank"
              rel="noreferrer"
              className="text-gray-800 hover:text-black underline dark:text-gray-200 dark:hover:text-white"
            >
              Site Auth
            </a>
          </p>
        </footer>
      </div>
    </>
  );
};
